import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import 'styles/d3/camera.scss'
import { closeIcon } from 'assets/assets'
import { Button } from 'components/UIElements'
import ImageEditor from './ImageEditor'
import WebcamCapture from './WebcamCapture'
import { logSentryEvent, SENTRY_MESSAGES } from 'utils/sentry'

const supportsWebcamCapture = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia

const Camera = (props) => {
  const {
    content,
    icon,
    iconAfter,
    className,
    deletePermission,
    onUpdate,
    openModal,
    closeModal,
    isEditable,
    cameraTip,
    initialImage,
    strings = {},
  } = props
  const [capturedImage, setCapturedImage] = useState(initialImage || '')
  const [editedImage, setEditedImage] = useState(
    initialImage ? { restored: true } : null,
  )
  const [imageType, setImageType] = useState('')
  const fileInputRef = useRef(null)
  let _className = ' camera-button'
  _className += className ? ` ${className}` : ''

  useEffect(() => {
    if (!editedImage?.file) return
    closeModal()
    const newUrl = URL.createObjectURL(editedImage.file)
    onUpdate({
      fileUrl: newUrl,
      imageType: editedImage.file?.type || imageType,
      file: editedImage.file,
      dataUrl: editedImage.base64,
    })
    setCapturedImage(newUrl)
  }, [editedImage])

  const openImgEditModal = (isDelete) => {
    openModal({
      className: 'edit-image-modal',
      content: (
        <ImageEditor
          img={capturedImage}
          setCroppedImage={setEditedImage}
          isRotative
          selfButton
          cameraTip={cameraTip}
          onChevron={() => {
            if (!isDelete) removeCapture()
            closeModal()
          }}
          onDelete={
            isDelete
              ? () => {
                  if (deletePermission) deletePermission(removeCapture)
                  else removeCapture()
                }
              : null
          }
        />
      ),
      noButtons: true,
      closeX: false,
      timesCloseX: false,
    })
  }

  useEffect(() => {
    if (capturedImage !== '' && !editedImage) {
      openImgEditModal(false)
    }
  }, [capturedImage, editedImage])

  const handleSelectedFile = (file) => {
    if (!file) return
    const newUrl = URL.createObjectURL(file)
    setImageType(file.type)
    setCapturedImage(newUrl)
    logSentryEvent({
      message: SENTRY_MESSAGES.permissionGranted,
      tags: {
        flow: 'permissions',
        action: 'granted',
        permission_type: 'camera',
      },
    })
  }

  const handleFileInputChange = (target) => {
    if (target.files && target.files.length !== 0) {
      /**
       * The onUpdate will be only be called when the image editing is
       * in the useEffect above. If it's called here, it will create duplicate
       * files in the submission question.
       */
      handleSelectedFile(target.files[0])
      target.value = ''
    }
  }

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const openWebcamModal = () => {
    openModal({
      className: 'webcam-capture-modal',
      heading: strings.take_photo || 'Take Photo',
      content: (
        <WebcamCapture
          cameraTip={cameraTip}
          strings={strings}
          onCancel={closeModal}
          onCapture={(file) => {
            closeModal()
            handleSelectedFile(file)
          }}
        />
      ),
      noButtons: true,
      closeX: false,
      timesCloseX: false,
    })
  }

  const openCaptureSourceModal = () => {
    if (!openModal || !supportsWebcamCapture()) {
      openFilePicker()
      return
    }

    openModal({
      className: 'camera-source-modal',
      heading: strings.add_photo || 'Add Photo',
      content: (
        <div className='camera-source-options flexed column center-aligned'>
          <Button
            className='camera-source-options__take-photo'
            content={strings.take_photo || 'Take Photo'}
            onClick={() => {
              closeModal()
              openWebcamModal()
            }}
          />
          <Button
            link
            className='camera-source-options__upload'
            content={strings.upload_from_device || 'Upload from device'}
            onClick={() => {
              closeModal()
              openFilePicker()
            }}
          />
        </div>
      ),
      cancelButton: strings.cancel_btn || 'Cancel',
      closeOnBackgroundClick: true,
      onCancel: closeModal,
      useWideButtons: true,
    })
  }

  const removeCapture = () => {
    setCapturedImage('')
    setEditedImage(null)
    setImageType('')
    onUpdate(null)
  }

  return !capturedImage ? (
    <>
      <button
        type='button'
        className={`flexed column center-justified${_className}`}
        onClick={openCaptureSourceModal}
      >
        {icon && !iconAfter && <i className={icon} />}
        <span className='button-content'>{content || 'Add Photo'}</span>
        {icon && iconAfter && <i className={icon} />}
      </button>
      <input
        ref={fileInputRef}
        className='camera-input'
        type='file'
        accept='image/jpeg,image/png,image/*'
        capture='environment'
        onChange={(e) => handleFileInputChange(e.target)}
      />
    </>
  ) : (
    (!isEditable || editedImage) && (
      <div className='captured-image-container'>
        <img
          className='captured-image'
          src={capturedImage}
          alt='Captured photo preview'
          onClick={() => openImgEditModal(true)}
        />
        <Button
          className='x-icon'
          link
          onClick={() =>
            deletePermission ? deletePermission(removeCapture) : removeCapture()
          }>
          {closeIcon()}
        </Button>
      </div>
    )
  )
}

Camera.propTypes = {
  className: PropTypes.string,
  content: PropTypes.string,
  deletePermission: PropTypes.func,
  icon: PropTypes.string,
  iconAfter: PropTypes.bool,
  initialImage: PropTypes.string,
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  onUpdate: PropTypes.func,
  cameraTip: PropTypes.string,
  strings: PropTypes.objectOf(PropTypes.string),
}

export default Camera
